export type ReloadValues = Record<string, unknown>;
export type ReloadChoice = "mine" | "latest";
export type ReloadChoices = Record<string, ReloadChoice>;

export interface ReloadConflict {
  key: string;
  base: unknown;
  mine: unknown;
  latest: unknown;
}

export interface ReloadMergePlan {
  values: ReloadValues;
  conflicts: ReloadConflict[];
  /** Fields changed by either editor compared with the opening snapshot. */
  changedKeys: string[];
  base: ReloadValues;
  mine: ReloadValues;
  latest: ReloadValues;
}

/** Structural equality for normalized JSON values; object key order is irrelevant.
 * Missing fields and undefined are equivalent; null remains distinct.
 */
export function reloadValuesEqual(left: unknown, right: unknown): boolean {
  if (Object.is(left, right)) return true;
  if (left === null || right === null || typeof left !== "object" || typeof right !== "object") return false;
  if (Array.isArray(left) || Array.isArray(right)) {
    return Array.isArray(left) && Array.isArray(right)
      && left.length === right.length
      && left.every((value, index) => reloadValuesEqual(value, right[index]));
  }
  const a = left as ReloadValues;
  const b = right as ReloadValues;
  return [...new Set([...Object.keys(a), ...Object.keys(b)])]
    .every((key) => reloadValuesEqual(a[key], b[key]));
}

function copyField(target: ReloadValues, source: ReloadValues, key: string) {
  if (Object.prototype.hasOwnProperty.call(source, key)) {
    Object.defineProperty(target, key, {
      value: source[key], enumerable: true, writable: true, configurable: true,
    });
  } else {
    delete target[key];
  }
}

/** Callers normalize field values and supply stable field/line identities. */
export function createReloadMerge(base: ReloadValues, mine: ReloadValues, latest: ReloadValues): ReloadMergePlan {
  const values: ReloadValues = {};
  const conflicts: ReloadConflict[] = [];
  const changedKeys: string[] = [];
  const keys = new Set([...Object.keys(base), ...Object.keys(mine), ...Object.keys(latest)]);
  for (const key of keys) {
    const mineUnchanged = reloadValuesEqual(mine[key], base[key]);
    const latestUnchanged = reloadValuesEqual(latest[key], base[key]);
    const same = reloadValuesEqual(mine[key], latest[key]);
    if (!mineUnchanged || !latestUnchanged) changedKeys.push(key);
    if (!mineUnchanged && !latestUnchanged && !same) {
      conflicts.push({ key, base: base[key], mine: mine[key], latest: latest[key] });
    }
    copyField(values, mineUnchanged || same ? latest : mine, key);
  }
  return { values, conflicts, changedKeys, base: { ...base }, mine: { ...mine }, latest: { ...latest } };
}

export function resolveReloadMerge(plan: ReloadMergePlan, choices: ReloadChoices): ReloadValues {
  const values = { ...plan.values };
  for (const conflict of plan.conflicts) {
    const choice = Object.prototype.hasOwnProperty.call(choices, conflict.key) ? choices[conflict.key] : undefined;
    if (choice !== "mine" && choice !== "latest") {
      throw new Error(`Choose which value to keep for "${conflict.key}".`);
    }
    copyField(values, plan[choice], conflict.key);
  }
  return values;
}